import * as Y from 'yjs';

/**
 * Comment threads — stored in the document's Y.Doc (so they sync across users)
 * under a top-level `comments` Y.Map, keyed by threadId. The `comment` mark on
 * the text range only carries the threadId; the thread content lives here.
 *
 * Shape per thread: { resolved: boolean, comments: Comment[] } stored as a plain
 * JSON object inside the Y.Map (threads are small + replaced wholesale on edit,
 * which is simpler than nesting Y.Arrays and fine for comment volumes).
 */

export interface Comment {
  id: string;
  author: string;
  text: string;
  createdAt: string;
}

export interface CommentThread {
  id: string;
  resolved: boolean;
  comments: Comment[];
}

const MAP_KEY = 'editor-comment-threads';

function threadsMap(doc: Y.Doc): Y.Map<unknown> {
  return doc.getMap(MAP_KEY);
}

function uuid(): string {
  // crypto.randomUUID is available in browsers + node test env.
  return crypto.randomUUID();
}

function nowIso(): string {
  return new Date().toISOString();
}

/** Create a new thread with its first comment. Returns the new threadId. */
export function createThread(doc: Y.Doc, first: { author: string; text: string }): string {
  const id = uuid();
  const thread: Omit<CommentThread, 'id'> = {
    resolved: false,
    comments: [{ id: uuid(), author: first.author, text: first.text, createdAt: nowIso() }],
  };
  threadsMap(doc).set(id, thread);
  return id;
}

/** Append a reply to an existing thread (no-op if missing). */
export function addReply(doc: Y.Doc, threadId: string, reply: { author: string; text: string }): void {
  const map = threadsMap(doc);
  const thread = map.get(threadId) as Omit<CommentThread, 'id'> | undefined;
  if (!thread) return;
  map.set(threadId, {
    ...thread,
    comments: [...thread.comments, { id: uuid(), author: reply.author, text: reply.text, createdAt: nowIso() }],
  });
}

/** Set the resolved flag (no-op if missing). */
export function setResolved(doc: Y.Doc, threadId: string, resolved: boolean): void {
  const map = threadsMap(doc);
  const thread = map.get(threadId) as Omit<CommentThread, 'id'> | undefined;
  if (!thread) return;
  map.set(threadId, { ...thread, resolved });
}

/** Remove a thread entirely. */
export function deleteThread(doc: Y.Doc, threadId: string): void {
  threadsMap(doc).delete(threadId);
}

/** Read one thread (null if missing). */
export function getThread(doc: Y.Doc, threadId: string): CommentThread | null {
  const thread = threadsMap(doc).get(threadId) as Omit<CommentThread, 'id'> | undefined;
  if (!thread) return null;
  return { id: threadId, ...thread };
}

/** All threads in the doc. */
export function listThreads(doc: Y.Doc): CommentThread[] {
  const map = threadsMap(doc);
  const out: CommentThread[] = [];
  map.forEach((thread, id) => {
    out.push({ id, ...(thread as Omit<CommentThread, 'id'>) });
  });
  return out;
}
